import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, fmt } from '../api';
import Chart from '../components/Chart';

const PLATFORM_COLORS: Record<string, string> = {
  meta: '#2f6af2',
  pinterest: '#d64862',
  reddit: '#ff6a25',
};

export default function CampaignDetail() {
  const { id } = useParams();
  const [data, setData] = useState<any | null>(null);

  useEffect(() => {
    if (!id) return;
    api.campaignDetail(id).then(setData);
  }, [id]);

  if (!data) return <div className="empty">Loading…</div>;
  const { campaign, adSets, daily } = data;

  const dayMap = new Map<string, any>();
  for (const r of daily) {
    if (!dayMap.has(r.day)) dayMap.set(r.day, { day: r.day, meta: 0, pinterest: 0, reddit: 0 });
    dayMap.get(r.day)[r.platform] = r.revenue_cents;
  }
  const chartData = [...dayMap.values()].sort((a, b) => a.day.localeCompare(b.day));

  const totalSpend = daily.reduce((s: number, r: any) => s + r.spend_cents, 0);
  const totalRevenue = daily.reduce((s: number, r: any) => s + r.revenue_cents, 0);
  const totalPurchases = daily.reduce((s: number, r: any) => s + r.purchases, 0);
  const totalClicks = daily.reduce((s: number, r: any) => s + r.clicks, 0);
  const totalImpr = daily.reduce((s: number, r: any) => s + r.impressions, 0);

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="/app/campaigns" style={{ fontSize: 13, color: 'var(--text-sub)' }}>← All campaigns</Link>
      </div>
      <h2 className="page-title">{campaign.name}</h2>
      <p className="page-sub">
        <span className={`badge badge-${campaign.status}`}>{campaign.status}</span>{' '}
        · daily budget {fmt.money(campaign.daily_budget_cents)} · launched {new Date(campaign.created_at).toLocaleDateString()}
      </p>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        <Kpi label="Spend" value={fmt.moneyShort(totalSpend)} />
        <Kpi label="Revenue" value={fmt.moneyShort(totalRevenue)} />
        <Kpi
          label="ROAS"
          value={totalSpend > 0 ? `${(totalRevenue / totalSpend).toFixed(2)}×` : '—'}
        />
        <Kpi label="Orders" value={fmt.num(totalPurchases)} />
        <Kpi label="CTR" value={totalImpr > 0 ? fmt.pct(totalClicks / totalImpr, 2) : '—'} />
      </div>

      <div className="card chart-wrap" style={{ marginBottom: 20 }}>
        <div className="card-title">Revenue over time</div>
        <Chart
          data={chartData}
          series={[
            { key: 'meta', color: PLATFORM_COLORS.meta, label: 'Meta' },
            { key: 'pinterest', color: PLATFORM_COLORS.pinterest, label: 'Pinterest' },
            { key: 'reddit', color: PLATFORM_COLORS.reddit, label: 'Reddit' },
          ]}
        />
      </div>

      <div className="card card-padded">
        <div className="card-title">Ad sets</div>
        <table className="data">
          <thead>
            <tr>
              <th>Platform</th>
              <th>External ID</th>
              <th>Status</th>
              <th className="num">Daily budget</th>
              <th>Targeting</th>
            </tr>
          </thead>
          <tbody>
            {adSets.map((a: any) => (
              <tr key={a.id}>
                <td>
                  <span className={`badge badge-${a.platform}`}>{a.platform}</span>
                </td>
                <td>
                  <code style={{ fontSize: 11 }}>{a.external_id ?? '—'}</code>
                </td>
                <td>
                  <span className={`badge badge-${a.status}`}>{a.status}</span>
                </td>
                <td className="num">{fmt.moneyShort(a.daily_budget_cents)}</td>
                <td style={{ fontSize: 11, color: 'var(--text-sub)' }}>
                  {JSON.stringify(JSON.parse(a.targeting_json)).slice(0, 90)}…
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
